import React, { useMemo } from "react";
import { filter, includes, join, map, reduce } from "lodash";
import Popover from "@radarly/components/cards/card/actions/scope/popover";
import { useLabelsFromClause } from "@radarly/core/clause/use-cases";
import { useContextualFocuses } from "@radarly/core/context/use-cases";
import { useProjectFacets } from "@radarly/core/facets/use-cases";
import { useCurrentProject } from "@radarly/core/project/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { useCurrentWorkspace } from "@radarly/core/workspace/use-cases";
import { Clause } from "@radarly/models/clause";
import { flattenInnerClauses } from "@radarly/models/clause/getter";
import {
  extractClauseWithoutIncludedFocuses,
  getFocusesOccurencesFromClause,
} from "../../facets";

interface IProps {
  clause: Clause;
}

function formatFocusClause(label, focuses, contextualFocuses) {
  return focuses.length === contextualFocuses.length || !focuses.length
    ? label + " (" + contextualFocuses.length + " queries)"
    : join(map(focuses, "label"), ", ");
}

function useClauseLabels(clause: Clause) {
  const locale = useCurrentUserLocale();
  const workspace = useCurrentWorkspace();
  const project = useCurrentProject();
  const contextualFocuses = useContextualFocuses();
  const popoverLabel = workspace ? workspace?.label : project.label;
  const focuses = useMemo(() => {
    const clauseFocuses = getFocusesOccurencesFromClause(clause);
    return map(clauseFocuses, (focusesEntry) => {
      return filter(contextualFocuses, (focus) =>
        includes(focusesEntry, focus.id)
      );
    });
  }, [clause, contextualFocuses]);

  const facets = useProjectFacets();
  const clauseWithoutFocuses = useMemo(() => {
    return extractClauseWithoutIncludedFocuses(clause);
  }, [clause]);
  const { labels, loading } = useLabelsFromClause(
    project.id,
    clauseWithoutFocuses,
    facets,
    locale
  );
  const scope = !focuses.length
    ? formatFocusClause(popoverLabel, focuses, contextualFocuses)
    : join(
        reduce(
          focuses,
          (acc, focusEntry) => {
            acc.push(
              formatFocusClause(popoverLabel, focusEntry, contextualFocuses)
            );
            return acc;
          },
          []
        ),
        " AND "
      );

  return {
    locale,
    labels,
    loading,
    scope,
  };
}

const RadarlyCardsActionScopePopover = ({ clause }: IProps) => {
  const { locale, labels, loading, scope } = useClauseLabels(
    flattenInnerClauses(clause)
  );
  return (
    <Popover
      locale={locale}
      clausesLabels={labels}
      loading={loading}
      scope={scope}
    />
  );
};

export default RadarlyCardsActionScopePopover;
